/**
 * body 直下へポータルした fixed 配置のポップオーバーを、アンカー要素の直下に置きつつ
 * ビューポート内に収めるための位置計算フック。
 *
 * 既定 (align='left') はアンカーの左端に左端を合わせて右下へ広がる。実寸を測ってから
 * 位置を決めるので、右にはみ出すときは右端で止め、左にはみ出すときは左余白で止め、
 * 下に入りきらなければアンカーの上側に出す (寄せ方の詳細は computePopoverPosition)。
 * リサイズ・スクロール (祖先のスクロール領域を含む) にも追従する。
 *
 * 計測前は null を返すので、呼び出し側は画面外 (-9999 など) に描画して一瞬のちらつきを避ける。
 */
import { useLayoutEffect, useState, type RefObject } from 'react';
import { computePopoverPosition } from '../utils/popoverPosition';

export interface AnchoredPopoverPosition {
  top: number;
  left: number;
}

export function useAnchoredPopoverPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  popoverRef: RefObject<HTMLElement | null>,
  align: 'left' | 'right' = 'left',
): AnchoredPopoverPosition | null {
  const [pos, setPos] = useState<AnchoredPopoverPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      const el = popoverRef.current;
      if (!anchor || !el) return;
      const a = anchor.getBoundingClientRect();
      setPos(
        computePopoverPosition({
          anchor: { top: a.top, bottom: a.bottom, left: a.left, right: a.right },
          size: { width: el.offsetWidth, height: el.offsetHeight },
          viewport: { width: window.innerWidth, height: window.innerHeight },
          align,
        }),
      );
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, popoverRef, align]);

  return pos;
}

/** 計測前に画面外へ逃がすための style 値。 */
export const OFFSCREEN_POPOVER_POSITION: AnchoredPopoverPosition = { top: -9999, left: -9999 };
